import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';
import type { BuilderMode } from '@/api/questionnaires/types';
import { useBreadcrumbLabel } from '@/hooks/useBreadcrumbLabel';
import { queryClient } from '@/lib/utils/queryClient';
import { useSidebarStore } from '@/store/sidebarStore';
import { InviteCompaniesDialog } from '../../../-components/InviteCompaniesDialog';
import { QuestionnaireDetailTabs } from '../../../-components/QuestionnaireDetailTabs';
import {
  builderDetailQueryOptions,
  useBuilderDetailQuery,
} from '../../../-hooks/useBuilderDetailQuery';
import { useInviteCompaniesController } from '../../../-hooks/useInviteCompaniesController';

const detailSearch = z.object({
  type: z.enum(['questionnaire', 'template']).optional(),
  // TanStack Router's default search parser auto-coerces `?isTemplate=true`
  // to the boolean `true`, but legacy callers (window.location pushes,
  // shared links) may still pass the literal string `"true"`. Accept
  // both and normalise in `resolveMode`.
  isTemplate: z.union([z.string(), z.boolean()]).optional(),
});

function resolveMode(search: {
  type?: 'questionnaire' | 'template' | undefined;
  isTemplate?: string | boolean | undefined;
}): BuilderMode {
  const isTemplate = search.isTemplate === true || search.isTemplate === 'true';
  if (search.type === 'template' || isTemplate) {
    return 'template';
  }
  return 'questionnaire';
}

export const Route = createFileRoute(
  '/(client)/questionnaires/(pages)/(detail)/$id',
)({
  validateSearch: detailSearch,
  loaderDeps: ({ search }) => ({ mode: resolveMode(search) }),
  // Detail-root URL (no trailing tab) redirects into the builder tab.
  //
  // In `beforeLoad`, not `loader`: this route has no index child, so the bare
  // path never resolves to anything renderable and its loader does not run —
  // the URL simply died. `beforeLoad` runs while the match is being built, so
  // it fires either way. The network company detail does the same.
  beforeLoad: ({ params, location }) => {
    const bare = `/questionnaires/${params.id}`;
    if (location.pathname === bare || location.pathname === `${bare}/`) {
      throw redirect({
        to: '/questionnaires/$id/edit',
        params: { id: params.id },
      });
    }
  },
  loader: ({ params, deps, location }) => {
    const isReviewRoute =
      location.pathname.endsWith('/review') ||
      location.pathname.endsWith('/review/');

    if (isReviewRoute) return undefined;
    return queryClient.ensureQueryData(
      builderDetailQueryOptions(deps.mode, params.id),
    );
  },
  component: QuestionnaireDetailLayout,
});

function QuestionnaireDetailLayout() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const mode = resolveMode(search);

  // The review-per-company tab brings its own side panel (company list),
  // so the global secondary sidebar would collide with it — collapse the
  // secondary panel while we're on that route. The builder, summary and
  // other tabs leave the user's preferred sidebar state alone (the builder
  // keeps whatever pinned/collapsed state it had before you entered).
  const pathname = useLocation({ select: l => l.pathname });
  const isReviewRoute =
    pathname.endsWith('/review') || pathname.endsWith('/review/');
  const { data: detail } = useBuilderDetailQuery(mode, id, {
    enabled: !isReviewRoute,
  });
  // Show the questionnaire name in the AppHeader breadcrumb instead of its id.
  useBreadcrumbLabel(id, detail?.name);
  const collapsePanel = pathname.endsWith('/review');
  const setPanelOpen = useSidebarStore(s => s.setPanelOpen);
  useEffect(() => {
    if (collapsePanel) {
      setPanelOpen(false);
      return () => setPanelOpen(true);
    }
  }, [collapsePanel, setPanelOpen]);

  const stats = detail?.statistics;
  const responsesCount = stats
    ? (stats.totalOpenRequests ?? 0) +
      (stats.totalInReviewRequests ?? 0) +
      (stats.totalCompletedRequests ?? 0) +
      (stats.totalRequestChanges ?? 0)
    : undefined;

  // Owns the invite-sheet state for the whole detail page. Mounted once
  // so the header button and the Summary KPI button both trigger the
  // same sheet via the shared `useInviteSheetStore` toggle.
  const invite = useInviteCompaniesController(id);

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-hidden'>
      <QuestionnaireDetailTabs
        id={id}
        isTemplate={mode === 'template'}
        {...(responsesCount !== undefined ? { responsesCount } : {})}
      />
      <Outlet />
      <InviteCompaniesDialog
        open={invite.open}
        onOpenChange={invite.setOpen}
        step={invite.step}
        onContinue={invite.goToConfigure}
        onBack={invite.goBackToSelect}
        onSubmit={invite.submit}
        isSubmitting={invite.isSubmitting}
        selectedCount={invite.selectedCount}
        resolveFacility={invite.resolveFacility}
        select={{
          companies: invite.companies,
          total: invite.total,
          loading: invite.isLoading,
          sorting: invite.sorting,
          onSortingChange: invite.onSortingChange,
          filters: invite.filters,
          onFiltersChange: invite.onFiltersChange,
          pagination: invite.pagination,
          onPaginationChange: invite.onPaginationChange,
          rowSelection: invite.rowSelection,
          onRowSelectionChange: invite.onRowSelectionChange,
          filterDefs: invite.selectFilterDefs,
          filterOptions: invite.filterOptions,
        }}
        configure={{
          companies: invite.configureCompanies,
          filters: invite.step2Filters,
          onFiltersChange: invite.setStep2Filters,
          rowSelection: invite.configureSelection,
          onRowSelectionChange: invite.setConfigureSelection,
          filterDefs: invite.configureFilterDefs,
          filterOptions: invite.configureFilterOptions,
          getAnsweringParty: invite.getAnsweringParty,
          onAnsweringPartyChange: invite.setAnsweringParty,
          onBulkAssignAnsweringParty: invite.bulkAssignAnsweringParty,
        }}
      />
    </div>
  );
}
